// src/components/Step3/Step3.jsx
import { CircularProgress } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import jwtDecode from 'jwt-decode'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import DatafinitiLogo from '../../HomeDR12/images/datafiniti-logo-white.svg'
import useAccountCreation from '../mutation/mutation'
import BaseAlert from '../../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import {
  businessPlans,
  peoplePlans,
  productPlans,
  propertyPlans
} from './plans'
import styles from './styles.module.css'
import { submitToHubSpot } from '../../../Api/hubspot'

const Step3 = ({
  onNext,
  isActive,
  token,
  clearDfpItemsFromLocalStorage,
  setNonTrialSelected
}) => {
  const [selectedPlans, setSelectedPlans] = useState({})
  const [plansSaved, setPlansSaved] = useState(false)
  const history = useHistory()
  const { mutateAccount, isLoading, errorMessage, successMessage } =
    useAccountCreation(history, clearDfpItemsFromLocalStorage)
  const [accountCreated, setAccountCreated] = useState(false)

  const redirectDashboard = () => {
    setAccountCreated(true)
    setTimeout(() => {
      let basePath = '/property-data' // Default path
      const dataTypes = Object.keys(selectedPlans)

      // Determine the base path based on selected data types in priority order
      if (dataTypes.includes('property')) {
        basePath = '/property-data'
      } else if (dataTypes.includes('people')) {
        basePath = '/people-data'
      } else if (dataTypes.includes('product')) {
        basePath = '/product-data'
      } else if (dataTypes.includes('business')) {
        basePath = '/business-data'
      }

      history.push(`${basePath}?signup=completed`)
      localStorage.removeItem('dfp/step2_data')
      localStorage.removeItem('dfp/signup_token')
    }, 2000)
  }

  const checkNonTrialPlans = plans => {
    const nonTrialSelected = Object.values(plans).some(
      plan => !plan.includes('trial')
    )
    setNonTrialSelected(nonTrialSelected)
  }

  useEffect(() => {
    if (!token) {
      localStorage.removeItem('dfp/step2_data')
    } else {
      const savedData = JSON.parse(localStorage.getItem('dfp/step2_data'))
      if (savedData && savedData.plan_ids) {
        const plans = savedData.plan_ids.reduce((acc, plan) => {
          const planType = plan.split('-')[0]
          acc[planType] = plan
          return acc
        }, {})
        setSelectedPlans(plans)
        setPlansSaved(true)
        checkNonTrialPlans(plans)
      }

      if (isActive) {
        const planData = JSON.parse(localStorage.getItem('dfp/planData'))
        if (planData) {
          setSelectedPlans(prevPlans => {
            const updatedPlans = {
              ...prevPlans,
              [planData.data_type]: planData.plan_id
            }
            checkNonTrialPlans(updatedPlans)
            return updatedPlans
          })
        }
      }
    }
  }, [token, isActive]) /* eslint-disable-line react-hooks/exhaustive-deps */

  const handlePlanChange = (e, field) => {
    const value = e.target.value

    setSelectedPlans(prevPlans => {
      const updatedPlans = { ...prevPlans }

      if (value === 'none') {
        delete updatedPlans[field]
      } else {
        updatedPlans[field] = value
      }

      checkNonTrialPlans(updatedPlans) // Check if non-trial plan is selected

      return updatedPlans
    })
    setPlansSaved(false) // Reset the saved state when changing plans
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const savedData = JSON.parse(localStorage.getItem('dfp/step2_data')) || {}
    savedData.plan_ids = Object.values(selectedPlans)
    localStorage.setItem('dfp/step2_data', JSON.stringify(savedData))
    setPlansSaved(true)

    const planData = JSON.parse(localStorage.getItem('dfp/planData')) || {}
    const Gclid = planData.Gclid

    const decodedToken = jwtDecode(token)
    const userData = {
      first_name: savedData.firstName,
      last_name: savedData.lastName,
      email: decodedToken.sub,
      organization: savedData.organization,
      password: savedData.password,
      plan_ids: Object.values(selectedPlans)
    }

    if (Gclid) {
      userData.Gclid = Gclid
    }

    const jwt = token

    if (userData.plan_ids.every(plan => plan.includes('trial'))) {
      const dataTypes = Object.keys(selectedPlans)
      const hubspotData = {
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        signupPlans: userData.plan_ids.join(','),
        signupType: 'trial',
        dataType: dataTypes
      }
      await submitToHubSpot(hubspotData)
      mutateAccount({ jwt, userData, redirectDashboard })
    } else {
      onNext()
    }
  }

  // Filter monthly and yearly plans for business
  const monthlyBusinessPlans = businessPlans
    .filter(plan => !plan.id.toLowerCase().includes('unlimited'))
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.price})`
        : `${plan.name} - ${plan.records} (${plan.price} per month)`
    }))

  const yearlyBusinessPlans = businessPlans
    .filter(
      plan => plan.yearlyPrice !== 'Free' && plan.yearlyPrice !== undefined
    )
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.yearlyPrice})`
        : `${plan.name} - ${plan.records} (${plan.yearlyPrice} per year)`
    }))

  // Filter monthly and yearly plans for property
  const monthlyPropertyPlans = propertyPlans
    .filter(plan => !plan.id.toLowerCase().includes('unlimited'))
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.price})`
        : `${plan.name} - ${plan.records} (${plan.price} per month)`
    }))

  const yearlyPropertyPlans = propertyPlans
    .filter(
      plan => plan.yearlyPrice !== 'Free' && plan.yearlyPrice !== undefined
    )
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.yearlyPrice})`
        : `${plan.name} - ${plan.records} (${plan.yearlyPrice} per year)`
    }))

  // Filter monthly and yearly plans for product
  const monthlyProductPlans = productPlans
    .filter(plan => !plan.id.toLowerCase().includes('unlimited'))
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.price})`
        : `${plan.name} - ${plan.records} (${plan.price} per month)`
    }))

  const yearlyProductPlans = productPlans
    .filter(
      plan => plan.yearlyPrice !== 'Free' && plan.yearlyPrice !== undefined
    )
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.yearlyPrice})`
        : `${plan.name} - ${plan.records} (${plan.yearlyPrice} per year)`
    }))

  // Filter monthly and yearly plans for people
  const monthlyPeoplePlans = peoplePlans
    .filter(plan => !plan.id.toLowerCase().includes('unlimited'))
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 100 records for 2 weeks (${plan.price})`
        : `${plan.name} - ${plan.records} (${plan.price} per month)`
    }))

  const yearlyPeoplePlans = peoplePlans
    .filter(
      plan =>
        plan.yearlyPrice !== 'Free' &&
        plan.yearlyPrice !== undefined &&
        !plan.id.toLowerCase().includes('unlimited')
    )
    .map(plan => ({
      ...plan,
      displayText: plan.id.includes('trial')
        ? `Trial - 1K records for 2 weeks (${plan.yearlyPrice})`
        : `${plan.name} - ${plan.records} (${plan.yearlyPrice} per year)`
    }))

  return (
    <div className={styles.container}>
      <img
        src={DatafinitiLogo}
        alt='DatafinitiLogo'
        className={styles.logo}
        style={{ opacity: isActive ? 1 : 0 }}
      />
      <div>
        {/* Display error message */}
        {/* {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )} */}
        <h1 className={styles.heading}>Step 3: Subscription</h1>
        <span className={styles.subHeading}>Select a subscription.</span>
        <form
          className={styles.form}
          onSubmit={isLoading || errorMessage ? null : handleSubmit}
        >
          <div className={styles.field}>
            <span htmlFor='property' className={styles.span}>
              Property
            </span>
            <select
              id='property'
              className={styles.select}
              disabled={!isActive}
              onChange={e => handlePlanChange(e, 'property')}
              value={selectedPlans.property || 'none'}
            >
              <option value='none'>None</option>
              <optgroup label='-- Monthly --'>
                {monthlyPropertyPlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
              <optgroup label='-- Yearly --'>
                {yearlyPropertyPlans.map(plan => (
                  <option key={plan.yearlyId} value={plan.yearlyId}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className={styles.field}>
            <span htmlFor='people' className={styles.span}>
              People
            </span>
            <select
              id='people'
              className={styles.select}
              disabled={!isActive}
              onChange={e => handlePlanChange(e, 'people')}
              value={selectedPlans.people || 'none'}
            >
              <option value='none'>None</option>
              <optgroup label='-- Monthly --'>
                {monthlyPeoplePlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
              <optgroup label='-- Yearly --'>
                {yearlyPeoplePlans.map(plan => (
                  <option key={plan.yearlyId} value={plan.yearlyId}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className={styles.field}>
            <span htmlFor='product' className={styles.span}>
              Product
            </span>
            <select
              id='product'
              className={styles.select}
              disabled={!isActive}
              onChange={e => handlePlanChange(e, 'product')}
              value={selectedPlans.product || 'none'}
            >
              <option value='none'>None</option>
              <optgroup label='-- Monthly --'>
                {monthlyProductPlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
              <optgroup label='-- Yearly --'>
                {yearlyProductPlans.map(plan => (
                  <option key={plan.yearlyId} value={plan.yearlyId}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className={styles.lastField}>
            <span htmlFor='business' className={styles.span}>
              Business
            </span>
            <select
              id='business'
              className={styles.select}
              disabled={!isActive}
              onChange={e => handlePlanChange(e, 'business')}
              value={selectedPlans.business || 'none'}
            >
              <option value='none'>None</option>
              <optgroup label='-- Monthly --'>
                {monthlyBusinessPlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
              <optgroup label='-- Yearly --'>
                {yearlyBusinessPlans.map(plan => (
                  <option key={plan.yearlyId} value={plan.yearlyId}>
                    {plan.displayText}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          {isLoading || errorMessage || accountCreated ? (
            <button disabled className={styles.spinnerDisabledButton}>
              {' '}
              <CircularProgress className={styles.circularProgress} />
            </button>
          ) : (
            <button
              type='submit'
              className={`${styles.button} ${
                plansSaved
                  ? styles.buttonSuccess
                  : isActive && Object.keys(selectedPlans).length > 0
                  ? styles.buttonEnabled
                  : styles.buttonDisabled
              }`}
              disabled={!isActive || Object.keys(selectedPlans).length === 0}
            >
              {plansSaved ? <CheckIcon /> : 'Continue'}
            </button>
          )}
        </form>

        {/* BaseAlert for success message */}
        <BaseAlert
          open={!!successMessage}
          // open={true}
          title='Account created'
          body='Redirecting you to the Datafiniti portal...'
          color='#48B966'
          onClose={() => {}}
          style={{
            position: 'absolute',
            left: '50%',
            top: 'revert',
            bottom: 'calc(-165px + var(--alert-offset))',
            right: '0%',
            width: '320px',
            height: '84px',
            transform: 'translate(0, 0) !important'
          }}
        />

        {successMessage && (
          <BaseAlert
            open={true}
            title='Account created'
            body='Redirecting you to the Datafiniti portal...'
            color='#48B966'
            onClose={() => {}}
            style={{
              position: 'absolute',
              left: '50%',
              top: 'revert',
              bottom: 'calc(-165px + var(--alert-offset))',
              right: '0%',
              width: '320px',
              height: '84px',
              transform: 'translate(0, 0) !important'
            }}
          />
        )}

        {/* Display error message as a BaseAlert */}
        {errorMessage && (
          <BaseAlert
            open={true}
            title='Error'
            body={errorMessage}
            color='#EF3688'
            onClose={() => {}}
            style={{
              position: 'absolute',
              left: '50%',
              top: 'revert',
              bottom: 'calc(-165px + var(--alert-offset))',
              right: '0%',
              width: '320px',
              height: '84px',
              transform: 'translate(0, 0) !important'
            }}
          />
        )}
      </div>
    </div>
  )
}

export default Step3
